import styled from '@emotion/styled'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import toastr from 'toastr'
import { useMutation } from '../../../api/graphql'
import { allFontsUrl, DEFAULT_FONT, FONT_GROUPS, fontFallback } from '../../../util/fonts'
import { BrandingSettings, UPDATE_BRANDING_MUTATION, UpdateBrandingResponse } from './BusinessCalendarQueries'
import { Hugeicon } from '../../../icons/Hugeicon'

// Validate hex color format (#RGB or #RRGGBB)
function isValidHexColor(color: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(color)
}

type Props = {
  organizationId: string
  brandingSettings: BrandingSettings
  contentFeed: {
    id: string
    databaseId: number
    title: string
    websiteUrl: string | null
  } | null
  onUpdate: () => void
}

const BrandingEditor: React.FC<Props> = ({ organizationId, brandingSettings, contentFeed, onUpdate }) => {
  const [primaryColor, setPrimaryColor] = useState(brandingSettings.primaryColor || '#6CC14C')
  const [extracting, setExtracting] = useState(false)
  const [detectedColors, setDetectedColors] = useState<string[]>([])
  const [calendarPageUrlSaved, setCalendarPageUrlSaved] = useState(contentFeed?.websiteUrl || '')

  // Split stored URL into protocol + domain for the split input
  const parseUrlParts = (url: string): { protocol: string; domain: string } => {
    if (url.startsWith('http://')) return { protocol: 'http://', domain: url.slice(7) }
    if (url.startsWith('https://')) return { protocol: 'https://', domain: url.slice(8) }
    return { protocol: 'https://', domain: url }
  }
  const initial = parseUrlParts(contentFeed?.websiteUrl || '')
  const [urlProtocol, setUrlProtocol] = useState(initial.protocol)
  const [urlDomain, setUrlDomain] = useState(initial.domain)
  const calendarPageUrl = urlDomain ? `${urlProtocol}${urlDomain}` : ''
  const [headingFont, setHeadingFont] = useState(brandingSettings.headingFont || DEFAULT_FONT)
  const [bodyFont, setBodyFont] = useState(brandingSettings.bodyFont || DEFAULT_FONT)
  const [previewKey, setPreviewKey] = useState(0)
  const [previewDevice, setPreviewDevice] = useState<'mobile' | 'tablet' | 'desktop'>('desktop')
  const [homepageDismissed, setHomepageDismissed] = useState(false)
  const colorInputRef = useRef<HTMLInputElement>(null)

  // Load ALL Google Fonts so dropdown options render in their actual typefaces
  useEffect(() => {
    const id = 'civiclift-all-fonts'
    if (document.getElementById(id)) return
    const link = document.createElement('link')
    link.id = id
    link.rel = 'stylesheet'
    link.href = allFontsUrl()
    document.head.appendChild(link)
  }, [])

  const [updateBranding, { loading: saving }] = useMutation<UpdateBrandingResponse>(UPDATE_BRANDING_MUTATION)

  const isColorValid = isValidHexColor(primaryColor)

  // Extract base origin from a URL (e.g., "yoursite.com/events" → "https://yoursite.com")
  const getBaseUrl = (url: string): string | null => {
    try {
      const parsed = new URL(url.startsWith('http') ? url : `https://${url}`)
      return parsed.origin
    } catch {
      return null
    }
  }

  const handleSave = async () => {
    if (!isColorValid) {
      toastr.error('Please enter a valid hex color (e.g., #1e3a5f)')
      return
    }

    const result = await updateBranding({
      variables: {
        organizationId,
        primaryColor,
      },
    })

    const response = result.data?.businessSubscriptionUpdateBranding
    if (response?.errors && response.errors.length > 0) {
      toastr.error(response.errors[0].message)
      return
    }

    toastr.success('Brand color applied')
    onUpdate()
    setPreviewKey((k) => k + 1)
  }

  const handleFontChange = async (type: 'heading' | 'body', font: string) => {
    if (type === 'heading') setHeadingFont(font)
    else setBodyFont(font)

    const variables: Record<string, string> = { organizationId }
    if (type === 'heading') variables.headingFont = font
    else variables.bodyFont = font

    const result = await updateBranding({ variables })
    const response = result.data?.businessSubscriptionUpdateBranding
    if (response?.errors && response.errors.length > 0) {
      toastr.error(response.errors[0].message)
      return
    }
    onUpdate()
    setPreviewKey((k) => k + 1)
  }

  // Auto-detect colors from the calendar page URL's base domain
  const handleDetectFromUrl = async (url: string) => {
    const baseUrl = getBaseUrl(url)
    if (!baseUrl) return

    setExtracting(true)
    try {
      const result = await updateBranding({
        variables: { organizationId, websiteUrl: baseUrl },
      })

      const response = result.data?.businessSubscriptionUpdateBranding
      if (response?.errors && response.errors.length > 0) {
        return
      }

      if (response?.detectedColors && response.detectedColors.length > 0) {
        setDetectedColors(response.detectedColors)
        setPrimaryColor(response.detectedColors[0])
      }
    } finally {
      setExtracting(false)
    }
  }

  const handleSaveCalendarUrl = async () => {
    const result = await updateBranding({
      variables: { organizationId, calendarPageUrl: calendarPageUrl || '' },
    })

    const response = result.data?.businessSubscriptionUpdateBranding
    if (response?.errors && response.errors.length > 0) {
      toastr.error(response.errors[0].message)
      return
    }

    setCalendarPageUrlSaved(calendarPageUrl)
    toastr.success(calendarPageUrl ? 'Calendar page URL saved' : 'Calendar page URL cleared')
    onUpdate()

    // Auto-detect brand colors from the saved URL
    if (calendarPageUrl) {
      handleDetectFromUrl(calendarPageUrl)
    }
  }

  const isValidHttpUrl = (url: string) => {
    try {
      const parsed = new URL(url.startsWith('http') ? url : `https://${url}`)
      return ['http:', 'https:'].includes(parsed.protocol)
    } catch {
      return false
    }
  }

  const looksLikeHomepage = (url: string) => {
    if (!url || !isValidHttpUrl(url)) return false
    try {
      const parsed = new URL(url.startsWith('http') ? url : `https://${url}`)
      return parsed.pathname === '/' || parsed.pathname === ''
    } catch {
      return false
    }
  }

  const calendarUrlInvalid = calendarPageUrl.length > 0 && !isValidHttpUrl(calendarPageUrl)
  const calendarUrlChanged = calendarPageUrl !== calendarPageUrlSaved
  const exampleEventLink = useMemo(
    () => (calendarPageUrl ? `${calendarPageUrl}${calendarPageUrl.includes('?') ? '&' : '?'}event=summer-concert` : null),
    [calendarPageUrl],
  )

  const feedBaseUrl = contentFeed ? `${window.location.origin}/feeds/${contentFeed.databaseId}/events` : null
  const feedUrl = feedBaseUrl ? `${feedBaseUrl}?preview=1&_=${previewKey}` : null
  // Show user's calendar page URL in the mockup address bar, or a placeholder
  const displayUrl = calendarPageUrl || 'https://yourwebsite.com/events'

  const loading = saving || extracting

  return (
    <div>
      {/* ── Calendar Page URL (full width, top) ── */}
      <SectionTitle>Calendar Page URL</SectionTitle>
      <SectionDescription>
        After embedding, set this so shared event links go to your site instead of CivicLift.
      </SectionDescription>

      <CalendarUrlRow>
        <UrlInputGroup>
          <ProtocolSelect
            value={urlProtocol}
            onChange={(e) => {
              setUrlProtocol(e.target.value)
              setHomepageDismissed(false)
            }}
          >
            <option value="https://">https://</option>
            <option value="http://">http://</option>
          </ProtocolSelect>
          <CalendarUrlInput
            type="text"
            value={urlDomain}
            onChange={(e) => {
              // Strip protocol if user pastes a full URL
              let val = e.target.value
              if (val.startsWith('https://')) {
                setUrlProtocol('https://')
                val = val.slice(8)
              } else if (val.startsWith('http://')) {
                setUrlProtocol('http://')
                val = val.slice(7)
              }
              setUrlDomain(val)
              setHomepageDismissed(false)
            }}
            placeholder="yoursite.com/events"
            onKeyDown={(e) => e.key === 'Enter' && !calendarUrlInvalid && calendarUrlChanged && handleSaveCalendarUrl()}
          />
        </UrlInputGroup>
        <SaveButton onClick={handleSaveCalendarUrl} disabled={loading || !calendarUrlChanged || calendarUrlInvalid}>
          {saving && !extracting ? '...' : 'Save'}
        </SaveButton>
      </CalendarUrlRow>

      {calendarUrlInvalid && (
        <UrlFeedback variant="error">Please enter a valid URL (e.g., yoursite.com/events)</UrlFeedback>
      )}

      {!calendarUrlInvalid && looksLikeHomepage(calendarPageUrl) && !homepageDismissed && (
        <UrlFeedback variant="warning">
          This looks like a homepage. Use your calendar page URL instead (e.g., yoursite.com<strong>/events</strong>).
          <DismissLink onClick={() => setHomepageDismissed(true)}>It is on my homepage</DismissLink>
        </UrlFeedback>
      )}

      {exampleEventLink && (!looksLikeHomepage(calendarPageUrl) || homepageDismissed) && (
        <UrlFeedback variant="info">
          Links will look like: <CalendarUrlExample>{exampleEventLink}</CalendarUrlExample>
        </UrlFeedback>
      )}

      {!calendarPageUrl && (
        <UrlFeedback variant="muted">
          Not set — shared links will point to CivicLift.
        </UrlFeedback>
      )}

      {/* ── Brand Color + Typography (inline) ── */}
      <SettingsRow>
        <SettingsGroup>
          <SectionTitle>Brand Color</SectionTitle>

          {detectedColors.length > 0 && (
            <DetectedLabel>
              <DetectedIcon />
              Detected from your website
            </DetectedLabel>
          )}

          <ColorRow>
            <ColorSwatch color={primaryColor} onClick={() => colorInputRef.current?.click()}>
              <HiddenColorInput
                ref={colorInputRef}
                type="color"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
              />
            </ColorSwatch>
            <HexInput
              type="text"
              value={primaryColor}
              onChange={(e) => setPrimaryColor(e.target.value)}
              placeholder="#6CC14C"
              maxLength={7}
              isInvalid={primaryColor.length > 0 && !isColorValid}
            />
            <SaveButton onClick={handleSave} disabled={loading || !isColorValid}>
              {saving ? '...' : 'Apply'}
            </SaveButton>
          </ColorRow>

          {detectedColors.length > 0 && (
            <SwatchRow>
              {detectedColors.map((color) => (
                <Swatch
                  key={color}
                  color={color}
                  selected={color.toLowerCase() === primaryColor.toLowerCase()}
                  onClick={() => setPrimaryColor(color)}
                  title={color}
                />
              ))}
            </SwatchRow>
          )}

          {extracting && (
            <UrlFeedback variant="muted">Detecting colors from your website...</UrlFeedback>
          )}
        </SettingsGroup>

        <SettingsGroup>
          <SectionTitle>Typography</SectionTitle>
          <FontRow>
            <FontPickerLabel>Heading</FontPickerLabel>
            <FontDropdown value={headingFont} onChange={(f) => handleFontChange('heading', f)} disabled={loading} />
            <FontPickerLabel>Body</FontPickerLabel>
            <FontDropdown value={bodyFont} onChange={(f) => handleFontChange('body', f)} disabled={loading} />
            {(headingFont !== DEFAULT_FONT || bodyFont !== DEFAULT_FONT) && (
              <ResetFontsButton
                onClick={() => {
                  handleFontChange('heading', DEFAULT_FONT)
                  handleFontChange('body', DEFAULT_FONT)
                }}
                title="Reset to Open Sans"
              >
                Reset
              </ResetFontsButton>
            )}
          </FontRow>
        </SettingsGroup>
      </SettingsRow>

      {/* ── Preview (full width, below settings) ── */}
      {feedUrl && (
        <>
          <PreviewHeader>
            <div>
              <SectionTitle style={{ margin: 0 }}>Preview</SectionTitle>
            </div>
            <DeviceToggleRow>
              <DeviceToggle active={previewDevice === 'mobile'} onClick={() => setPreviewDevice('mobile')}>
                <MobileIcon />
                Mobile
              </DeviceToggle>
              <DeviceToggle active={previewDevice === 'tablet'} onClick={() => setPreviewDevice('tablet')}>
                <TabletIcon />
                Tablet
              </DeviceToggle>
              <DeviceToggle active={previewDevice === 'desktop'} onClick={() => setPreviewDevice('desktop')}>
                <DesktopIcon />
                Desktop
              </DeviceToggle>
            </DeviceToggleRow>
          </PreviewHeader>
          <PreviewContainer>
            {previewDevice === 'desktop' ? (
              <BrowserMockup>
                <BrowserToolbar>
                  <BrowserDots>
                    <BrowserDot color="#ec6a5e" />
                    <BrowserDot color="#f4bf4f" />
                    <BrowserDot color="#61c554" />
                  </BrowserDots>
                  <BrowserAddressBar>{displayUrl}</BrowserAddressBar>
                </BrowserToolbar>
                <BrowserContent>
                  <WebsiteHeader>
                    <HeaderLogo />
                    <HeaderNav>
                      <HeaderNavItem style={{ width: 48 }} />
                      <HeaderNavItem style={{ width: 56 }} />
                      <HeaderNavItem style={{ width: 40 }} />
                      <HeaderNavItem style={{ width: 52 }} />
                    </HeaderNav>
                  </WebsiteHeader>
                  <IframeWrapper>
                    <iframe key={previewKey} src={feedUrl} title="Calendar preview" style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
                  </IframeWrapper>
                </BrowserContent>
              </BrowserMockup>
            ) : previewDevice === 'tablet' ? (
              <TabletMockup>
                <TabletScreen>
                  <WebsiteHeader>
                    <HeaderLogo />
                    <HeaderNav>
                      <HeaderNavItem style={{ width: 40 }} />
                      <HeaderNavItem style={{ width: 48 }} />
                      <HeaderNavItem style={{ width: 36 }} />
                    </HeaderNav>
                  </WebsiteHeader>
                  <IframeWrapper>
                    <iframe key={previewKey} src={feedUrl} title="Calendar preview" style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
                  </IframeWrapper>
                </TabletScreen>
              </TabletMockup>
            ) : (
              <PhoneMockup>
                <PhoneNotch />
                <PhoneScreen>
                  <WebsiteHeaderMobile>
                    <HeaderLogoMobile />
                    <HamburgerIcon>
                      <span /><span /><span />
                    </HamburgerIcon>
                  </WebsiteHeaderMobile>
                  <IframeWrapper>
                    <iframe key={previewKey} src={feedUrl} title="Calendar preview" style={{ width: '100%', height: '100%', border: 'none', display: 'block' }} />
                  </IframeWrapper>
                </PhoneScreen>
                <PhoneHomeBar />
              </PhoneMockup>
            )}
          </PreviewContainer>
        </>
      )}
    </div>
  )
}

/* ── Custom Font Dropdown ── */

type FontDropdownProps = {
  value: string
  onChange: (font: string) => void
  disabled?: boolean
}

const FontDropdown: React.FC<FontDropdownProps> = ({ value, onChange, disabled }) => {
  const [open, setOpen] = useState(false)
  const [focusIndex, setFocusIndex] = useState(-1)
  const wrapperRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Flat list of all fonts for keyboard navigation
  const allFonts = useMemo(() => FONT_GROUPS.flatMap(([, fonts]) => fonts), [])

  const close = useCallback(() => {
    setOpen(false)
    setFocusIndex(-1)
  }, [])

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) close()
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open, close])

  // Scroll focused item into view
  useEffect(() => {
    if (!open || focusIndex < 0 || !listRef.current) return
    const items = listRef.current.querySelectorAll('[data-font-option]')
    items[focusIndex]?.scrollIntoView({ block: 'nearest' })
  }, [focusIndex, open])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return
    if (!open) {
      if (['Enter', ' ', 'ArrowDown'].includes(e.key)) {
        e.preventDefault()
        setOpen(true)
        setFocusIndex(allFonts.indexOf(value))
      }
      return
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setFocusIndex((i) => Math.min(i + 1, allFonts.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setFocusIndex((i) => Math.max(i - 1, 0))
        break
      case 'Enter':
      case ' ':
        e.preventDefault()
        if (focusIndex >= 0) {
          onChange(allFonts[focusIndex])
          close()
        }
        break
      case 'Escape':
        e.preventDefault()
        close()
        break
    }
  }

  const fallback = fontFallback(value)

  return (
    <FDWrapper ref={wrapperRef} onKeyDown={handleKeyDown}>
      <FDTrigger
        type="button"
        onClick={() => !disabled && setOpen(!open)}
        disabled={disabled}
        style={{ fontFamily: `'${value}', ${fallback}` }}
        aria-expanded={open}
        aria-haspopup="listbox"
      >
        <FDTriggerText>{value}</FDTriggerText>
        <FDChevron open={open}>
          <Hugeicon name="arrow-down" size={12} />
        </FDChevron>
      </FDTrigger>

      {open && (
        <FDDropdown ref={listRef} role="listbox">
          {FONT_GROUPS.map(([group, fonts]) => (
            <React.Fragment key={group}>
              <FDGroupLabel>{group}</FDGroupLabel>
              {fonts.map((font) => {
                const idx = allFonts.indexOf(font)
                const isSelected = font === value
                const isFocused = idx === focusIndex
                const fb = fontFallback(font)
                return (
                  <FDOption
                    key={font}
                    data-font-option
                    role="option"
                    aria-selected={isSelected}
                    selected={isSelected}
                    focused={isFocused}
                    style={{ fontFamily: `'${font}', ${fb}` }}
                    onMouseEnter={() => setFocusIndex(idx)}
                    onClick={() => {
                      onChange(font)
                      close()
                    }}
                  >
                    {isSelected && <FDCheck>✓</FDCheck>}
                    {font}
                  </FDOption>
                )
              })}
            </React.Fragment>
          ))}
        </FDDropdown>
      )}
    </FDWrapper>
  )
}

/* ── Icons ── */

const MobileIcon: React.FC = () => (
  <Hugeicon name="smartphone" size={14} />
)

const TabletIcon: React.FC = () => (
  <Hugeicon name="tablet" size={14} />
)

const DesktopIcon: React.FC = () => (
  <Hugeicon name="computer" size={14} />
)

export default BrandingEditor

/* ── Settings row (inline flex) ── */

const SettingsRow = styled.div`
  display: flex;
  gap: 4rem;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  margin-bottom: 28px;

  @media (max-width: 700px) {
    flex-direction: column;
    gap: 20px;
  }
`

const SettingsGroup = styled.div``

/* ── Section header ── */

const SectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: #102638;
  margin: 0 0 8px;
`

const SectionDescription = styled.p`
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 12px;
  line-height: 1.4;
`

/* ── Color row ── */

const ColorRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

const ColorSwatch = styled.div<{ color: string }>`
  width: 34px;
  height: 34px;
  border-radius: 6px;
  background: ${(p) => p.color};
  border: 1px solid rgba(0, 0, 0, 0.1);
  cursor: pointer;
  position: relative;
  flex-shrink: 0;
  transition: box-shadow 0.15s ease;

  &:hover {
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.06);
  }
`

const HiddenColorInput = styled.input`
  position: absolute;
  width: 0;
  height: 0;
  opacity: 0;
  pointer-events: none;
`

const HexInput = styled.input<{ isInvalid?: boolean }>`
  && {
    width: 80px;
    height: 34px;
    padding: 0 8px;
    font-size: 13px;
    font-family: monospace;
    border: 1px solid ${({ isInvalid }) => (isInvalid ? '#ef4444' : '#d1d5db')};
    border-radius: 6px;
    color: #111827;
    outline: none;
    box-sizing: border-box;
  }

  &:focus {
    border-color: ${({ isInvalid }) => (isInvalid ? '#ef4444' : '#3b82f6')};
    box-shadow: 0 0 0 3px ${({ isInvalid }) => (isInvalid ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)')};
  }
`

const DetectedLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #059669;
  font-weight: 500;
  margin-bottom: 6px;
`

const DetectedIcon: React.FC = () => (
  <Hugeicon name="checkmark-circle" size={14} />
)

/* ── Detected swatches ── */

const SwatchRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`

const Swatch = styled.button<{ color: string; selected: boolean }>`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 2px solid ${(p) => (p.selected ? '#1e3a5f' : 'transparent')};
  background: ${(p) => p.color};
  cursor: pointer;
  outline: none;
  box-shadow: ${(p) => (p.selected ? '0 0 0 2px white, 0 0 0 4px #1e3a5f' : '0 1px 3px rgba(0,0,0,0.12)')};
  transition: all 0.15s ease;
  flex-shrink: 0;

  &:hover {
    transform: scale(1.1);
  }
`

/* ── Save ── */

const SaveButton = styled.button`
  background: #1e3a5f;
  color: white;
  font-size: 13px;
  font-weight: 600;
  height: 34px;
  padding: 0 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: #2d4a6f;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

/* ── Font picker ── */

const FontRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const FontPickerLabel = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  white-space: nowrap;
  flex-shrink: 0;
`

const ResetFontsButton = styled.button`
  height: 34px;
  padding: 0 10px;
  font-size: 12px;
  color: #6b7280;
  background: none;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`

/* ── Custom Font Dropdown Styles ── */

const FDWrapper = styled.div`
  position: relative;
  width: 180px;
  flex-shrink: 0;
`

const FDTrigger = styled.button`
  width: 100%;
  height: 34px;
  padding: 0 28px 0 10px;
  font-size: 13px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  color: #111827;
  background: white;
  outline: none;
  cursor: pointer;
  box-sizing: border-box;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const FDTriggerText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FDChevron = styled.span<{ open: boolean }>`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%) ${(p) => (p.open ? 'rotate(180deg)' : 'rotate(0)')};
  color: #6b7280;
  pointer-events: none;
  transition: transform 0.15s ease;
  display: flex;
  align-items: center;
`

const FDDropdown = styled.div`
  position: absolute;
  z-index: 50;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 320px;
  overflow-y: auto;
  background: #1a2332;
  border: 1px solid #2d3a4a;
  border-radius: 8px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
  padding: 4px 0;
`

const FDGroupLabel = styled.div`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  padding: 10px 14px 4px;
  user-select: none;
`

const FDOption = styled.button<{ selected: boolean; focused: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 14px;
  font-size: 15px;
  border: none;
  background: ${(p) => (p.selected ? '#2563eb' : p.focused ? '#2d3a4a' : 'transparent')};
  color: ${(p) => (p.selected ? 'white' : '#e5e7eb')};
  cursor: pointer;
  text-align: left;
  outline: none;
  transition: background 0.1s ease;

  &:hover {
    background: ${(p) => (p.selected ? '#2563eb' : '#2d3a4a')};
  }
`

const FDCheck = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: white;
  width: 16px;
  flex-shrink: 0;
`

/* ── Calendar Page URL ── */

const CalendarUrlRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
`

const UrlInputGroup = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
`

const ProtocolSelect = styled.select`
  && {
    height: 34px;
    padding: 0 4px 0 8px;
    font-size: 12px;
    color: #6b7280;
    background: #f9fafb;
    border: 1px solid #d1d5db;
    border-right: none;
    border-radius: 6px 0 0 6px;
    outline: none;
    cursor: pointer;
    box-sizing: border-box;
    flex-shrink: 0;
    -webkit-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath fill='%236b7280' d='M0 0l5 6 5-6z'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 6px center;
    padding-right: 18px;
  }

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
    z-index: 1;
  }
`

const CalendarUrlInput = styled.input`
  && {
    flex: 1;
    height: 34px;
    padding: 0 10px;
    font-size: 13px;
    border: 1px solid #d1d5db;
    border-radius: 0 6px 6px 0;
    color: #111827;
    outline: none;
    background: white;
    box-sizing: border-box;
    min-width: 0;
  }

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }
`


const CalendarUrlExample = styled.code`
  font-size: 11px;
  color: #374151;
  background: #f3f4f6;
  padding: 1px 4px;
  border-radius: 3px;
  word-break: break-all;
`

const UrlFeedback = styled.div<{ variant: 'error' | 'warning' | 'info' | 'muted' }>`
  font-size: 12px;
  line-height: 1.4;
  margin-bottom: 4px;
  padding: ${(p) => (p.variant === 'warning' ? '8px 10px' : '0')};
  border-radius: ${(p) => (p.variant === 'warning' ? '6px' : '0')};
  background: ${(p) => {
    if (p.variant === 'warning') return '#fef3c7'
    return 'transparent'
  }};
  border: ${(p) => (p.variant === 'warning' ? '1px solid #fde68a' : 'none')};
  color: ${(p) => {
    switch (p.variant) {
      case 'error': return '#dc2626'
      case 'warning': return '#92400e'
      case 'info': return '#6b7280'
      case 'muted': return '#9ca3af'
    }
  }};
`

const DismissLink = styled.button`
  background: none;
  border: none;
  color: #92400e;
  font-size: 12px;
  font-weight: 600;
  text-decoration: underline;
  cursor: pointer;
  padding: 0;
  margin-left: 6px;

  &:hover {
    color: #78350f;
  }
`

/* ── Preview ── */

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
`

const DeviceToggleRow = styled.div`
  display: flex;
  gap: 6px;
`

const DeviceToggle = styled.button<{ active: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  height: 32px;
  padding: 0 12px;
  font-size: 11px;
  font-weight: 600;
  border: 1.5px solid ${(p) => (p.active ? '#1e3a5f' : '#e5e7eb')};
  border-radius: 6px;
  cursor: pointer;
  background: ${(p) => (p.active ? '#f0f4f8' : 'white')};
  color: ${(p) => (p.active ? '#1e3a5f' : '#6b7280')};
  transition: all 0.15s ease;

  &:hover {
    border-color: ${(p) => (p.active ? '#1e3a5f' : '#9ca3af')};
    color: ${(p) => (p.active ? '#1e3a5f' : '#374151')};
  }
`

const PreviewContainer = styled.div`
  width: calc(100vw - 192px);
  margin-left: calc(-1 * (100vw - 192px - 100%) / 2);
  display: flex;
  justify-content: center;
  padding: 32px 5% 48px;
  background: #f0f2f5;
  min-height: 700px;

  @media (max-width: 1023px) {
    width: calc(100vw - 55px);
    margin-left: calc(-1 * (100vw - 55px - 100%) / 2);
  }
`

/* ── Browser mockup (Desktop) ── */

const BrowserMockup = styled.div`
  width: 100%;
  background: #e8eaed;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
`

const BrowserToolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #e8eaed;
`

const BrowserDots = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`

const BrowserDot = styled.div<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${(p) => p.color};
`

const BrowserAddressBar = styled.div`
  flex: 1;
  background: white;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 11px;
  color: #6b7280;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const BrowserContent = styled.div`
  height: 900px;
  background: white;
  border-radius: 0 0 10px 10px;
  overflow: clip;
  display: flex;
  flex-direction: column;
`

/* ── Tablet mockup ── */

const TabletMockup = styled.div`
  width: 810px;
  max-width: 100%;
  background: #d4d6da;
  border-radius: 24px;
  padding: 20px;
  border: 1px solid #c0c2c6;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
`

/* ── Phone mockup ── */

const PhoneMockup = styled.div`
  width: 390px;
  max-width: 100%;
  background: #d4d6da;
  border-radius: 40px;
  padding: 14px;
  border: 1px solid #c0c2c6;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  align-items: center;
`

const PhoneNotch = styled.div`
  width: 100px;
  height: 6px;
  background: #c0c2c6;
  border-radius: 3px;
  margin-bottom: 8px;
`

const TabletScreen = styled.div`
  height: 1024px;
  background: white;
  border-radius: 8px;
  overflow: clip;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const PhoneScreen = styled.div`
  height: 844px;
  background: white;
  border-radius: 28px;
  overflow: clip;
  width: 100%;
  display: flex;
  flex-direction: column;
`

const PhoneHomeBar = styled.div`
  width: 120px;
  height: 5px;
  background: #c0c2c6;
  border-radius: 3px;
  margin-top: 8px;
`

/* ── Website Header Wireframe ── */

const WebsiteHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 32px;
  background: repeating-linear-gradient(
    -45deg,
    #f3f4f6,
    #f3f4f6 8px,
    #e9ebee 8px,
    #e9ebee 16px
  );
  border-bottom: 1px solid #d1d5db;
  position: relative;

  &::after {
    content: "Your Website's Header";
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 12px;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    padding: 4px 12px;
    border-radius: 4px;
    border: 1px dashed #9ca3af;
    white-space: nowrap;
  }
`

const HeaderLogo = styled.div`
  width: 100px;
  height: 28px;
  background: #d1d5db;
  border-radius: 4px;
`

const HeaderNav = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`

const HeaderNavItem = styled.div`
  height: 8px;
  background: #d1d5db;
  border-radius: 2px;
`

const WebsiteHeaderMobile = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: repeating-linear-gradient(
    -45deg,
    #f3f4f6,
    #f3f4f6 6px,
    #e9ebee 6px,
    #e9ebee 12px
  );
  border-bottom: 1px solid #d1d5db;
  position: relative;

  &::after {
    content: "Your Website's Header";
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    font-size: 10px;
    font-weight: 600;
    color: #6b7280;
    background: #f3f4f6;
    padding: 3px 8px;
    border-radius: 3px;
    border: 1px dashed #9ca3af;
    white-space: nowrap;
  }
`

const HeaderLogoMobile = styled.div`
  width: 60px;
  height: 20px;
  background: #d1d5db;
  border-radius: 4px;
`

const HamburgerIcon = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 4px;

  span {
    display: block;
    width: 16px;
    height: 2px;
    background: #9ca3af;
    border-radius: 1px;
  }
`

const IframeWrapper = styled.div`
  flex: 1;
  overflow: hidden;
`
